import { registerView, View, ViewComponent } from "@frui.ts/views";
import { observer } from "mobx-react-lite";
import React, { useEffect, useRef, useState } from "react";
import OrderWizardViewModel from "viewModels/eshop/order/orderWizardModel";
import Stepper from "./components/stepper";
import Button from "@edaz/edazkit/Button";
import { useIdleTimer } from "react-idle-timer";
import { Col, Jumbotron, Row } from "react-bootstrap";
import { IconType } from "@edaz/edazkit/Icon";
import formatMillisecondsToTime from "utils/formatMilisecondsToTime";
import Tooltip from "controls/tooltip";
import IdleModal from "views/common/idleModal/idleModal";
import AlertWithInfoIcon from "views/common/alertWithInfoIcon/alertWithInfoIcon";
import LicenseViewModel from "viewModels/eshop/order/steps/licenseViewModel";
import LoadingModal from "views/common/loadingModal/loadingModal";
import GooglePay3DSecureModal from "./components/googlePay3DSecureModal";
import { PaymentError } from "views/common/error/paymentError";
import ErrorCard from "views/common/error/errorCard";
import { isMobile as reactIsMobile } from "react-device-detect";
import { SuccessAlert } from "views/common/success/successAlert";

const OrderView: ViewComponent<OrderWizardViewModel> = observer(({ vm }) => {
  const tg = (navigationName: string) => vm.localizationService.translateGeneral(`eshop.order.${navigationName}`);
  const idleModalTranslate = (code: string) => vm.localizationService.translateGeneral(`eshop.idle.${code}`);
  const modalTranslate = (code: string) => vm.localizationService.translateGeneral(`eshop.card.${code}`);
  const tpf = (code: string) => vm.localizationService.translateGeneral(`eshop.payment.status.failure.${code}`);
  const error = (code: string) => vm.localizationService.translateGeneral(`errors.${code}`);
  const { getRemainingTime } = useIdleTimer({
    onIdle: vm.handleOnIdle,
    timeout: vm.IDLE_TIMER_TIMEOUT,
    debounce: vm.IDLE_TIMER_DEBOUNCE,
  });

  const [timeLeft, setTimeLeft] = useState(vm.IDLE_TIMER_TIMEOUT + vm.IDLE_PROMPT_TIMEOUT);
  const [countdown, setCountdown] = useState(10);

  const isMobile = () => {
    const userAgentIndicatesMobile = reactIsMobile;
    const screenWidthIndicatesMobile = window.innerWidth < 768;

    return userAgentIndicatesMobile || screenWidthIndicatesMobile;
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft(Math.ceil(getRemainingTime() / 1000) * 1000 + vm.IDLE_PROMPT_TIMEOUT);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (vm.paymentIsComplete) {
      setCountdown(10);

      const intervalId = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(intervalId);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      const timeoutId = setTimeout(() => {
        vm.closePaymentComplete();
      }, 10000);

      return () => {
        clearInterval(intervalId);
        clearTimeout(timeoutId);
      };
    }
  }, [vm.paymentIsComplete]);

  let timer;
  if (vm.IDLE_TIMER_TIMEOUT + vm.IDLE_PROMPT_TIMEOUT - timeLeft >= vm.IDLE_TIMER_DELAY && getRemainingTime() > 0) {
    timer = (
      <div className="d-flex flex-grow-1 justify-content-end order-timer-wrapper">
        <Tooltip id="order-timer-tooltip" placement="bottom-end" tooltip={tg("timerTooltip")} show={true}>
          <div className="order-timer">
            <span>{formatMillisecondsToTime(timeLeft)}</span>
          </div>
        </Tooltip>
      </div>
    );
  }
  const cancelButton = (
    <Button
      type="button"
      variant="light-danger"
      size="sm"
      className="mt-3 mt-sm-0 pr-3 pl-3"
      onClick={vm.resetOrder}
      iconLeftVariant="light-danger"
      iconLeft={IconType.Cross}>
      {tg("cancel")}
    </Button>
  );
  const mobileCancelButton = (
    <Button
      type="button"
      variant="light-danger"
      size="sm"
      className="mt-3 mt-sm-0 pr-3 pl-3"
      onClick={vm.resetOrder}
      iconLeftVariant="light-danger"
      iconLeft={IconType.Cross}>
      {tg("cancel")}
    </Button>
  );

  return (
    <>
      <div className="mx-auto p-0 p-sm-3 col-lg-10 col-xxl-8" id="singleEshop">
        <View vm={vm.payGateOutagesViewModel} />
        <View vm={vm.eshopOutagesViewModel} />
        {vm.showPaymentErrorMessage && (
          <PaymentError
            title={vm.paymentErrorTitle}
            description={tpf("infoCount")}
            showLink={vm.showPaymentErrorLink}
            buttonText={tpf("buttonText")}
            onClick={vm.closePaymentFailureModal}
          />
        )}
        {vm.paymentIsComplete && (
          <SuccessAlert
            title={tg("successPaymentRedirectTitle")}
            description={tg("successPaymentRedirectMessage")}
            showLink={false}
            buttonText={tpf("buttonText")}
            onClick={vm.closePaymentComplete}
          />
        )}
        {!vm.showPaymentErrorMessage && !vm.paymentIsComplete && (
          <Jumbotron className="p-lg-6 p-s-0 p-xl-6">
            <div className="p-lg-4 p-s-0 p-xl-4">
              <div className="d-flex flex-column flex-wrap justify-content-between px-lg-6">
                {vm.showInlineErrorMessage && vm.errorMessage && <ErrorCard message={error(vm.errorMessage)} sticky={true} />}
                <Stepper current={vm.navigationStep ?? 1} statusSteps={vm.statusNavigation} />
                {vm.activeChild instanceof LicenseViewModel && (
                  <Row>
                    <AlertWithInfoIcon variant="warning" className="mx-3 mt-2 mb-4">
                      <span className="font-size-sm">{vm.translateCountryWarning}</span>
                    </AlertWithInfoIcon>
                  </Row>
                )}
                <Row>
                  {isMobile() && (
                    <Col sm={6} xl={6} className="d-flex align-items-center justify-content-end" style={{ order: 2 }}>
                      {timer ? timer : mobileCancelButton}
                    </Col>
                  )}
                  <Col sm={6} xl={6} className="d-flex align-items-center justify-content-start" style={{ order: 1 }}>
                    <h3 className="m-0">
                      {vm.activeChild && vm.activeChild.navigationName ? tg(`${vm.activeChild.navigationName}.title`) : ""}
                    </h3>
                  </Col>
                  {!isMobile() && (
                    <Col sm={6} xl={6} className="d-flex align-items-center justify-content-end" style={{ order: 2 }}>
                      {timer ? timer : cancelButton}
                    </Col>
                  )}
                </Row>

                <div className="mt-4 mt-lg-7 mt-xl-7">
                  <View vm={vm.activeChild} />
                </div>

                <LoadingModal translate={modalTranslate} isVisible={vm.showPaymentLoadingModal} />

                <GooglePay3DSecureModal isVisible={vm.showGooglePay3DSecureModal} url={vm.googlePay3DSecureUrl} />
              </div>
            </div>
          </Jumbotron>
        )}
      </div>
      <IdleModal
        translate={idleModalTranslate}
        isVisible={vm.showIdleModal}
        remainTime={vm.counter}
        resetIdle={vm.closeIdleModal}
      />
    </>
  );
});

registerView(OrderView, OrderWizardViewModel);
